import {
  Flex,
  Button,
  Text,
  Image,
  Accordion,
  Textarea,
  Input,
  DrawerOverlay,
  DrawerFooter,
  DrawerBody,
  FormControl,
  FormErrorMessage,
  useDisclosure,
  useToast,
  ExpandedIndex,
  VStack,
  Box,
  FormLabel,
  UseDisclosureReturn,
} from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import { SYLA_EARLIEST_EVENT_DATE } from "@syla/shared/types/helpers/constants";
import { LedgerType } from "@syla/shared/types/models/LedgerBase";
import { TransactionType } from "@syla/shared/types/models/TransactionBase";

import { NewLedger } from "@syla/shared/types/requests/TransactionRequests";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { isDesktop } from "react-device-detect";
import {
  useForm,
  FormProvider,
  useFieldArray,
  Controller,
} from "react-hook-form";
import { parseErrorToUserMessage } from "../../../../api/errors/parsers/parseErrorToUserMessage";
import { AccountTimeZone } from "../../../../constants/AccountTimeZone";
import { adjustAmount } from "../../../../helper/forms/stringAsNumberInputField";
import { thumbnailExtractor } from "../../../../helper/thumbnailExtractor";
import {
  getInLedgerTypeOptionsByTransactionType,
  getOutLedgerTypeOptionsByTransactionType,
  getFeeLedgerTypeOptionsByTransactionType,
} from "../../../../helper/transaction/getLedgerType";
import { getTransactionTypeOptions } from "../../../../helper/transaction/getTransactionTypeOptions";
import { useGetWalletOptions } from "../../../../hooks/wallet/useGetWalletOptions";
import completeIcon from "../../../../images/icons/complete.svg";
import { addTransaction } from "../../../../store/actions/addTransaction";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { useQueryAssetOptions } from "../../../../store/useQueryAssets";
import { NewTransactionForm } from "../../../../types/order/addOrderForm";
import { AppDrawer } from "../../../AppDrawer";
import { DatePicker } from "../../../atoms/datePicker/DatePicker";
import { startOfMinuteInTimeZone } from "../../../atoms/datePicker/tzDateUtils";
import { SectionHeading } from "../../../atoms/Headings";
import { AppDrawerContent } from "../../../modals/AppDrawerContent";
import { SingleSelector } from "../../../selector/SingleSelector";
import { LedgerInputs } from "./LedgerInputs";
import { TransactionAccordionItem } from "./TransactionAccordionItem";

export const AddTransactionDrawer = ({
  disclosure,
}: {
  disclosure: UseDisclosureReturn;
}): JSX.Element => {
  // Add transaction form state
  const { isOpen: isAddTransactionCompleted, onOpen: completeAddTransaction } =
    useDisclosure();

  // generate assets list
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const { data: assetOptionsResult } = useQueryAssetOptions();
  const assetOptions = assetOptionsResult ?? [];

  const toast = useToast();

  const setAddTransactionError = (message: string) =>
    toast({
      title: "Error adding transaction",
      description: message,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });

  const defaultTransactionType = TransactionType.Trade;

  // hook form setup
  const methods = useForm<NewTransactionForm>({
    defaultValues: {
      type: defaultTransactionType,
      outLedgers: [
        {
          type: getOutLedgerTypeOptionsByTransactionType(
            defaultTransactionType,
          )[0].value,
        },
      ],
      inLedgers: [
        {
          type: getInLedgerTypeOptionsByTransactionType(
            defaultTransactionType,
          )[0].value,
        },
      ],
      feeLedgers: [{}],
    },
  });

  // hook form methods
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  /** Set first applicable ledgerType as default for each mandatory ledger, or clear if not applicable */
  const setDefaultLedgerTypes = useCallback(
    (transactionType: TransactionType) => {
      if (transactionType == TransactionType.Transaction) {
        setValue(`inLedgers.0.type`, null as any);
      } else {
        setValue(
          `inLedgers.0.type`,
          getInLedgerTypeOptionsByTransactionType(transactionType)?.at(0)
            ?.value as LedgerType,
        );
      }

      if (transactionType == TransactionType.Transaction) {
        setValue(`outLedgers.0.type`, null as any);
      } else {
        setValue(
          `outLedgers.0.type`,
          getOutLedgerTypeOptionsByTransactionType(transactionType)?.at(0)
            ?.value as LedgerType,
        );
      }

      // clear fee
      setValue(`feeLedgers.0.type`, null as any);
    },
    [setValue],
  );

  // hook form fieldArray for outLedgers
  const { fields: outLedgerFields } = useFieldArray({
    name: "outLedgers",
    control,
  });

  // hook form fieldArray for inLedgers
  const { fields: inLedgerFields } = useFieldArray({
    name: "inLedgers",
    control,
  });

  // hook form fieldArray for feeLedgers, NOTE: transactions no longer have the fee ledgers array, the hook form will keep it to maintain a single form for fees and just build the alt in ledgers and alt out ledgers array in the on submit handler
  const { fields: feeLedgerFields } = useFieldArray({
    name: "feeLedgers",
    control,
  });

  // hook form watch over transaction type
  const transactionType = watch("type");
  const inLedger = watch("inLedgers.0");
  const outLedger = watch("outLedgers.0");

  const eitherOrRule = transactionType == TransactionType.Transaction;

  // reset ledger types when changing tx type
  useEffect(() => {
    setDefaultLedgerTypes(transactionType);
  }, [transactionType, setDefaultLedgerTypes]);

  // hook form onSubmit
  const onSubmit = async (data: NewTransactionForm) => {
    if (!data.type || !data.walletId || !data.date) {
      setAddTransactionError("Form is incomplete");
      return;
    }

    const {
      type,
      walletId,
      date,
      description,
      transactionHash,
      outLedgers,
      inLedgers,
      feeLedgers,
    } = data;

    // check fee ledgers

    const ledgerHasRequiredValues = (ledger: NewLedger | undefined) =>
      ledger?.type && ledger?.assetId && ledger?.amount;

    // if fee ledgers doesn't have any real values, remove the default empty object to make it an empty array
    if (feeLedgers.length === 1 && !ledgerHasRequiredValues(feeLedgers[0])) {
      feeLedgers.pop();
    }

    if (
      eitherOrRule &&
      !ledgerHasRequiredValues(inLedgers.at(0)) &&
      !ledgerHasRequiredValues(outLedgers.at(0))
    ) {
      setAddTransactionError("Please fill in all required In or Out values");
      return;
    }

    // convert to absolute amounts
    const mapLedger = (ledger: NewLedger): NewLedger => ({
      ...ledger,
      amount:
        ledger.amount ?
          Decimal.from(adjustAmount(ledger.amount)).abs().toString()
        : ledger.amount,
      customMarketValue:
        ledger.customMarketValue ?
          {
            assetId: ledger.customMarketValue.assetId,
            marketValue: adjustAmount(ledger.customMarketValue.marketValue),
          }
        : undefined,
    });

    try {
      await addTransaction({
        accountId,
        date,
        description: "",
        transaction: {
          walletId,
          type,
          date,
          description,
          transactionHash,
          outLedgers: outLedgers.filter(ledgerHasRequiredValues).map(mapLedger),
          inLedgers: inLedgers.filter(ledgerHasRequiredValues).map(mapLedger),
          feeLedgers: feeLedgers.map(mapLedger),
        },
      });
      completeAddTransaction();
    } catch (error) {
      setAddTransactionError(parseErrorToUserMessage(error).description);
    }
  };

  // query for user wallets data
  const walletOptions = useGetWalletOptions(accountId)?.data;

  const transactionTypeOptions = useMemo(
    () =>
      getTransactionTypeOptions.filter(
        (transactionType) =>
          ![TransactionType.AddLiquidity, TransactionType.RemoveLiquidity]
            .map((t) => t.toString())
            .includes(transactionType.value),
      ),
    [],
  );

  const [expandedIndexes, setExpandedIndexes] = useState<ExpandedIndex>([]);

  // set default expand when tx type changes
  useEffect(() => {
    [TransactionType.Send, TransactionType.Receive].includes(transactionType) ?
      setExpandedIndexes([0])
      // : transactionType == TransactionType.Transaction
      // ? setExpandedIndexes([])
    : setExpandedIndexes([0, 1]);
  }, [transactionType]);

  // at least one must be selected when either or, otherwise each is required

  const ledgerTypeRequiredRule = useCallback(
    (targetValue) =>
      (eitherOrRule && (inLedger.type || outLedger.type)) || targetValue ?
        undefined
      : eitherOrRule ? "Select at least one ledger type"
      : "Please select a ledger type",
    [eitherOrRule, inLedger.type, outLedger.type],
  );

  return (
    <AppDrawer
      isOpen={disclosure.isOpen}
      placement="right"
      onClose={disclosure.onClose}
      size="sm"
      closeOnOverlayClick={!isSubmitting}
    >
      <DrawerOverlay />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <AppDrawerContent>
            {!isAddTransactionCompleted && (
              <Box p="20px 0 10px 20px">
                <SectionHeading>Add Transaction</SectionHeading>
              </Box>
            )}
            <DrawerBody p="0">
              {/* -------------------- When add transaction is completed ------------------------- */}
              {isAddTransactionCompleted ?
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  h="100%"
                >
                  <Image src={completeIcon} alt="Complete" h="70px" />
                  <Text
                    fontSize="2rem"
                    w="80%"
                    fontWeight="bold"
                    color="black.900"
                    mb="30px"
                    textAlign="center"
                  >
                    Your transaction has been imported
                  </Text>
                </Flex>
              : <>
                  <Flex direction="column" px="24px" w="100%" mb="20px">
                    <FormControl
                      mb="10px"
                      isRequired
                      isDisabled={isSubmitting}
                      isInvalid={errors.type !== undefined}
                    >
                      <FormLabel>Type of transaction</FormLabel>
                      <Controller
                        control={control}
                        name="type"
                        rules={{ required: "Please select a transaction type" }}
                        render={({ field }) => (
                          <SingleSelector
                            value={field.value ?? undefined}
                            onChange={(value) => {
                              field.onChange(value ?? "");
                            }}
                            options={transactionTypeOptions}
                            placeholder="Choose transaction type"
                            buttonProps={{
                              width: "full",
                            }}
                            popoverProps={{
                              matchWidth: true,
                            }}
                          />
                        )}
                      />
                      {errors.type && (
                        <FormErrorMessage>
                          {errors.type.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                    <FormControl
                      mb="10px"
                      isRequired
                      isDisabled={isSubmitting}
                      isInvalid={errors.walletId !== undefined}
                    >
                      <FormLabel>Wallet</FormLabel>
                      <Controller
                        control={control}
                        name="walletId"
                        rules={{ required: "Please select a wallet" }}
                        render={({ field: { onChange, value } }) => (
                          <SingleSelector
                            moveSelectedToTop
                            value={value}
                            onChange={(selection) => onChange(selection)}
                            options={walletOptions || []}
                            enableSearch
                            placeholder="Choose wallet"
                            autoSelectSingleItem
                            buttonProps={{
                              width: "full",
                            }}
                            popoverProps={{
                              matchWidth: true,
                            }}
                          />
                        )}
                      />
                      {errors.walletId && (
                        <FormErrorMessage>
                          {errors.walletId.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                    {/* Label is outside the form control, otherwise a label click will cause the date popover to open*/}
                    <FormLabel>Date and time</FormLabel>
                    <FormControl
                      mb="10px"
                      isRequired
                      isDisabled={isSubmitting}
                      isInvalid={errors.date !== undefined}
                    >
                      <Controller
                        control={control}
                        name="date"
                        rules={{ required: "Please specify date and time" }}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || undefined}
                            earliestDate={SYLA_EARLIEST_EVENT_DATE}
                            latestDate={startOfMinuteInTimeZone(
                              new Date(),
                              AccountTimeZone,
                            )}
                            onChange={(date) => field.onChange(date)}
                            showTime={true}
                            timeZone={AccountTimeZone}
                          />
                        )}
                      />
                      {errors.date && (
                        <FormErrorMessage>
                          {errors.date.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  </Flex>
                  <Accordion
                    allowMultiple
                    allowToggle
                    borderColor="transparent"
                    // auto expand mandatory parts
                    index={expandedIndexes}
                    onChange={setExpandedIndexes}
                  >
                    {/* -------------------- Out ------------------------- */}
                    {transactionType &&
                      transactionType !== TransactionType.Receive &&
                      outLedgerFields.map((field, index) => (
                        <TransactionAccordionItem title="Out" key={field.id}>
                          <VStack w="full" spacing={2}>
                            <Controller
                              control={control}
                              name={`outLedgers.${index}.type`}
                              rules={{
                                required: ledgerTypeRequiredRule(
                                  outLedger.type,
                                ),
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <SingleSelector
                                  moveSelectedToTop
                                  value={field.value || undefined}
                                  onChange={(value) => {
                                    field.onChange(value ?? "");
                                  }}
                                  options={getOutLedgerTypeOptionsByTransactionType(
                                    transactionType,
                                  )}
                                  enableSearch
                                  isInvalid={!!error}
                                  enableDeselect={eitherOrRule}
                                  placeholder={
                                    error?.message ?? "Select ledger type"
                                  }
                                  buttonProps={{
                                    width: "full",
                                  }}
                                  popoverProps={{
                                    matchWidth: true,
                                  }}
                                />
                              )}
                            />
                            <LedgerInputs
                              name={`outLedgers[${index}]`}
                              list={assetOptions}
                              isRequired={!eitherOrRule || !!outLedger.type}
                            />
                          </VStack>
                        </TransactionAccordionItem>
                      ))}
                    {/* -------------------- In ------------------------- */}
                    {transactionType &&
                      transactionType !== TransactionType.Send &&
                      inLedgerFields.map((field, index) => (
                        <TransactionAccordionItem title="In" key={field.id}>
                          <VStack w="full" spacing={2}>
                            <Controller
                              control={control}
                              name={`inLedgers.${index}.type`}
                              rules={{
                                required: ledgerTypeRequiredRule(inLedger.type),
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <SingleSelector
                                  moveSelectedToTop
                                  value={field.value || undefined}
                                  onChange={(value) => {
                                    field.onChange(value ?? "");
                                  }}
                                  options={getInLedgerTypeOptionsByTransactionType(
                                    transactionType,
                                  )}
                                  enableSearch
                                  isInvalid={!!error}
                                  enableDeselect={eitherOrRule}
                                  placeholder={
                                    error?.message ?? "Select ledger type"
                                  }
                                  buttonProps={{
                                    width: "full",
                                  }}
                                  popoverProps={{
                                    matchWidth: true,
                                  }}
                                />
                              )}
                            />
                            <LedgerInputs
                              name={`inLedgers[${index}]`}
                              list={assetOptions}
                              isRequired={!eitherOrRule || !!inLedger.type}
                            />
                          </VStack>
                        </TransactionAccordionItem>
                      ))}
                    {transactionType &&
                      // any ledger options available
                      getFeeLedgerTypeOptionsByTransactionType(transactionType)
                        .length > 0 &&
                      feeLedgerFields.map((field, index) => (
                        <TransactionAccordionItem
                          title={
                            watch(`feeLedgers.${index}.type`) ?
                              thumbnailExtractor(
                                watch(`feeLedgers.${index}.type`),
                              ).name
                            : index === 0 ?
                              "Fee"
                            : "New Entry"
                          }
                          key={field.id}
                          optional
                        >
                          <Flex w="100%" direction="column">
                            <VStack w="full" spacing={2}>
                              <Controller
                                control={control}
                                name={`feeLedgers.${index}.type`}
                                render={({ field }) => (
                                  <SingleSelector
                                    moveSelectedToTop
                                    enableDeselect
                                    value={field.value || undefined}
                                    placeholder="Select ledger type"
                                    onChange={(value) => {
                                      field.onChange(value ?? "");
                                    }}
                                    options={getFeeLedgerTypeOptionsByTransactionType(
                                      transactionType,
                                    )}
                                    enableSearch
                                    buttonProps={{
                                      width: "full",
                                    }}
                                    popoverProps={{
                                      matchWidth: true,
                                    }}
                                  />
                                )}
                              />
                              <LedgerInputs
                                name={`feeLedgers[${index}]`}
                                list={assetOptions}
                                isRequired={false}
                              />
                            </VStack>
                          </Flex>
                        </TransactionAccordionItem>
                      ))}
                    <TransactionAccordionItem title="Description" optional>
                      <Textarea
                        {...register("description")}
                        placeholder="Type your description in here"
                      />
                    </TransactionAccordionItem>
                    <TransactionAccordionItem title="Transaction hash" optional>
                      <Input
                        {...register("transactionHash")}
                        type="text"
                        placeholder="Paste transaction hash here"
                        autoFocus={isDesktop}
                      />
                    </TransactionAccordionItem>
                  </Accordion>
                </>
              }
            </DrawerBody>
            <DrawerFooter>
              <Flex direction="column" w="100%">
                {!isAddTransactionCompleted && (
                  <Button
                    type={isAddTransactionCompleted ? "button" : "submit"}
                    onClick={() => {
                      if (!isAddTransactionCompleted) {
                        return;
                      }
                      disclosure.onClose();
                    }}
                    colorScheme="primary"
                    mb="10px"
                    isLoading={isSubmitting}
                  >
                    Add transaction
                  </Button>
                )}
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={disclosure.onClose}
                  isDisabled={isSubmitting}
                >
                  Close
                </Button>
              </Flex>
            </DrawerFooter>
          </AppDrawerContent>
        </form>
      </FormProvider>
    </AppDrawer>
  );
};
